import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Construction, PendingActions, ReportProblem, CheckCircle, Inventory2, Speed } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { installationsApi } from '../../api/installations.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

export function InstallationDashboardPage() {
  const dash = useQuery({ queryKey: ['installations', 'dashboard'], queryFn: () => installationsApi.dashboard() });
  const d = dash.data;
  const statusData = d ? Object.entries(d.byStatus).map(([label, value]) => ({ label: label.replace(/_/g, ' '), value, color: '#1e40af' })) : [];

  return (
    <>
      <PageHeader title="Installation Dashboard" subtitle="Installation pipeline, SLA breaches and delays." />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Pending" value={n(d?.pending)} icon={<Construction />} hint={`${n(d?.total)} total`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#7c3aed" label="Ongoing" value={n(d?.ongoing)} icon={<PendingActions />} hint={`${n(d?.completed)} completed`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#0891b2" label="Material Ready" value={n(d?.materialReady)} icon={<Inventory2 />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="SLA Performance" value={`${n(d?.slaPerformance)}%`} icon={<Speed />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard color="#1e40af" label="Open Installations" value={n(d?.open)} icon={<Construction />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard color="#d97706" label="Pending Handover" value={n(d?.pendingHandover)} icon={<PendingActions />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard color="#dc2626" label="SLA Breached" value={n(d?.slaBreached)} icon={<ReportProblem />} hint={`${n(d?.overdue)} overdue`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard color="#16a34a" label="Closed" value={n(d?.closed)} icon={<CheckCircle />} /></Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Installations by Status" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>{statusData.length ? <MiniBarChart data={statusData} valueFormatter={n} /> : <EmptyState title="No installations yet" dense />}</CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Recent Installations" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(d?.recent.length ?? 0) === 0 ? <EmptyState title="No installations yet" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Request</TableCell><TableCell>Priority</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(d?.recent ?? []).map((r) => (
                      <TableRow key={r.id} hover><TableCell>{r.requestCode}</TableCell><TableCell>{r.priority}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
